import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Printer,
  Copy,
  Check,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import TermsBlockRenderer from "./TermsBlockRenderer";

export default function TermsContent({
  activeSection,
  sections = [],
  onSelectSection = () => {},
}) {
  const [copied, setCopied] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(null);

  if (!activeSection) {
    return (
      <div className="terms-content-card text-center py-5">
        <p className="text-muted">Please select a section from the navigation.</p>
      </div>
    );
  }

  const currentIndex = sections.findIndex((s) => s.id === activeSection.id);
  const prevSection = currentIndex > 0 ? sections[currentIndex - 1] : null;
  const nextSection =
    currentIndex < sections.length - 1 ? sections[currentIndex + 1] : null;

  const handleCopyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${activeSection.id}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <article
      className="terms-content-card"
      id={`panel-${activeSection.id}`}
      role="tabpanel"
      aria-labelledby={`tab-${activeSection.id}`}
    >
      {/* Breadcrumbs & Category Badge */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0 small">
            <li className="breadcrumb-item">
              <Link to="/" className="text-decoration-none text-muted">
                Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <span className="text-muted">Legal &amp; Policies</span>
            </li>
            <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
              Section {currentIndex + 1}
            </li>
          </ol>
        </nav>

        <span className="badge bg-light text-primary border border-primary-subtle d-inline-flex align-items-center gap-1">
          <ShieldCheck size={13} />
          {activeSection.category || "General Terms"}
        </span>
      </div>

      {/* Document Section Title */}
      <h1 className="terms-content-title mb-1">{activeSection.tabTitle}</h1>
      {activeSection.docTitle && activeSection.docTitle.length > 0 && (
        <div className="text-muted small fw-medium mb-3">
          {activeSection.docTitle.join(" • ")}
        </div>
      )}

      {/* Metadata Bar & Actions */}
      <div className="terms-content-meta">
        <div className="d-flex align-items-center flex-wrap gap-3">
          {activeSection.lastUpdated && (
            <span className="terms-content-meta-item">
              <Clock size={14} className="text-muted" />
              <span>Last updated: {activeSection.lastUpdated}</span>
            </span>
          )}
          {activeSection.effectiveDate && (
            <span className="terms-content-meta-item">
              <Calendar size={14} className="text-muted" />
              <span>Effective: {activeSection.effectiveDate}</span>
            </span>
          )}
        </div>

        <div className="terms-actions-bar ms-auto">
          <button
            type="button"
            className="terms-action-btn"
            onClick={handleCopyLink}
            title="Copy direct link to this section"
            aria-label="Copy direct link"
          >
            {copied ? (
              <>
                <Check size={14} className="text-success" />
                <span className="text-success">Copied!</span>
              </>
            ) : (
              <>
                <Copy size={14} />
                <span>Share</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="terms-action-btn"
            onClick={handlePrint}
            title="Print this document section"
            aria-label="Print section"
          >
            <Printer size={14} />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* Formatted Legal Clauses / Blocks */}
      <TermsBlockRenderer blocks={activeSection.blocks} />

      {/* Previous / Next Pagination Cards (Shopify style) */}
      <div className="terms-pagination-grid">
        {prevSection ? (
          <button
            type="button"
            className="terms-pagination-card text-start"
            onClick={() => onSelectSection(prevSection.id)}
          >
            <span className="terms-pagination-sub">
              <ChevronLeft size={14} /> Previous Section
            </span>
            <span className="terms-pagination-title">{prevSection.tabTitle}</span>
          </button>
        ) : (
          <div />
        )}

        {nextSection && (
          <button
            type="button"
            className="terms-pagination-card text-end ms-auto w-100"
            onClick={() => onSelectSection(nextSection.id)}
          >
            <span className="terms-pagination-sub justify-content-end">
              Next Section <ChevronRight size={14} />
            </span>
            <span className="terms-pagination-title">{nextSection.tabTitle}</span>
          </button>
        )}
      </div>

      {/* Feedback Widget */}
      <div className="terms-feedback-widget">
        <p className="small text-muted mb-2">
          Was this section clear and informative?
        </p>
        {feedbackGiven ? (
          <span className="small text-success fw-semibold">
            Thank you for helping us improve our documentation!
          </span>
        ) : (
          <div className="d-flex justify-content-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
              onClick={() => setFeedbackGiven("yes")}
            >
              <ThumbsUp size={14} /> Yes
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
              onClick={() => setFeedbackGiven("no")}
            >
              <ThumbsDown size={14} /> No
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

